<template>
  <div :id="'bundle-carousel-' + id" class="contentful-bundle-feed" :class="{'bleedout': data.fullWidth, 'to-edge': data.fullWidth}">
    <div class="bundle-carousel-container swiper-margin">
      <div v-if="bundles && bundles.length" class="carousel-container ctf-indent">
        <div class="carousel" ref="bundlesEl">
          <div v-for="bundle in bundles" :key="bundle.id" class="carousel-bundle" :class="{ 'carousel-bundle-mobile': overrideTileWidthForMobile }" :data-bundle-id="bundle.id">
            <ProductSetTile :set="bundle" @imageLoaded="attachScrollListeners"/>
          </div>
        </div>
        <div
          v-if="scrollRightEnabled || scrollLeftEnabled"
          class="carousel-button-next"
          :class="{ disabled: !scrollRightEnabled}"
          @click="scrollRight"
        >
          <span class="af af-chevron-right"></span>
        </div>
        <div
          v-if="scrollRightEnabled || scrollLeftEnabled"
          class="carousel-button-prev"
          :class="{ disabled: !scrollLeftEnabled}"
          @click="scrollLeft"
        >
          <span class="af af-chevron-left"></span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useContentful, contentfulProps } from '@/compositions/useContentful.js';
import { watch, ref, onUnmounted } from 'vue';
import useResize from '@/compositions/useResize.js';
import useAsyncComputed from '@/compositions/useAsyncComputed.js';
import useProductBundle from '@/compositions/useProductBundle';
import ProductSetTile from '../../../components/sets/ProductSetTile.vue';
import { computed} from 'vue';

let carouselIdInc = 0;

const TILE_WIDTH = 430;

export default {
  props: contentfulProps,
  components: {
    ProductSetTile,
  },
  data() {
    return {
      id: null,
    }
  },
  async setup(p, c) {
    const { data, init } = useContentful('bundleFeed', p, c);
    const { loadProductBundles, loadProductBundlesProductIds } = useProductBundle();
    const roomNames = computed(() => data.value.roomNames && data.value.roomNames.length !== 0 ? data.value.roomNames : null);
    const productIdList = computed(() => data.value.productIdList && data.value.productIdList.length !== 0 ? data.value.productIdList : null);
    const loaded = ref(false);
    const { width } = useResize();
    const scrollLeftEnabled = ref(false);
    const scrollRightEnabled = ref(false);
    const bundlesEl = ref(null);
    const overrideTileWidthForMobile = ref(false);

    onUnmounted(() => {
      if (bundlesEl.value) {
        bundlesEl.value.removeEventListener('scroll', updateArrows);
      }
    });

    const [bundles, varIsBeingLoaded, initBundles] = useAsyncComputed( 
      async () => {
        let bundles = [];
        if (roomNames.value && roomNames.value.length) {
          bundles = await loadProductBundles(
            { page: 0, //
              count: 5,
              rooms: roomNames.value,
            }
          ); 
        } else {
          bundles = await loadProductBundlesProductIds(productIdList.value);
        }
        return bundles?.productBundles ? bundles.productBundles.filter(bundle => bundle.fileKey && bundle.mobileFileKey) : [];
      }
    );

    await Promise.all([init, initBundles]);

    function updateArrows() {
      if (bundlesEl.value) {
        scrollLeftEnabled.value = bundlesEl.value.scrollLeft > 0;
        scrollRightEnabled.value = bundlesEl.value.scrollLeft + bundlesEl.value.offsetWidth < bundlesEl.value.scrollWidth;
      }
    }

    function scrollLeft() {
      if (bundlesEl.value) {
        bundlesEl.value.scrollBy({
          left: -490,
          behavior: 'smooth',
        });
      }
    }

    function scrollRight() {
      if (bundlesEl.value) {
        bundlesEl.value.scrollBy({
          left: 490,
          behavior: 'smooth',
        });
      }
    }

    watch(width, (newWidth) => {
      overrideTileWidthForMobile.value = newWidth < TILE_WIDTH;
      updateArrows();
    });

    return {
      updateArrows,
      scrollLeftEnabled,
      scrollRightEnabled,
      scrollLeft,
      scrollRight,
      bundlesEl,
      data,
      bundles,
      overrideTileWidthForMobile,
      width,
      loaded,
    };
  },
  mounted() {
    this.id = carouselIdInc++;
  },
  methods: {
    /* This method will be called once the Images have loaded
     and is a good place to add the listeners. Doing this in onMounted() is not
     very reliable since this.bundlesEl may or may not be set at the time it runs*/
    attachScrollListeners() {
      if (!this.loaded && this.bundlesEl) {
        this.updateArrows();
        this.bundlesEl.addEventListener('scroll', this.updateArrows);
        this.loaded = true;
      }
    }
  }
};
</script>

<style lang="postcss">
@import '@/pixel/style/variables';
@import '@/pixel/style/mixins';

.contentful-bundle-feed {
  @media (max-width: calc($mobile-breakpoint - 1px)) {
    margin-left: -20px;
    margin-right: -20px;
    .carousel-bundle:first-child {
      margin-left: 20px;
    }
  }
  .bundle-carousel-container {
    min-height: 350px;
    margin-bottom: 40px;
    @media (min-width: $mobile-breakpoint) and (max-width: calc($large-breakpoint - 1px)) {
      margin-top: 20px !important;
    }
    @media (max-width: calc($mobile-breakpoint - 1px)) {
      margin-top: 0px !important;
    }

    .carousel-container {
      position: relative;
      margin-top: var(--space-4);
    }

    .carousel {
      display: flex;
      width: inherit;
      overflow-x: scroll;
      overflow-y: hidden;
      white-space: nowrap;
      scrollbar-width: none;
      &::-webkit-scrollbar {
        display: none;
      }
    }

    .carousel-bundle:last-child {
      margin-right: 0px;
      @media (max-width: calc($mobile-breakpoint - 1px)) {
        margin-right: 10px;
      }
    }

    .carousel-bundle {
      position: relative;
      white-space: normal;
      margin-right: 10px;
      width: 430px;
      flex-basis: 430px;
      flex-shrink: 0;
      flex-grow: 0;
      .product-set-info {
        color: var(--font-color);
        .product-set-title {
          line-height: var(--line-height-14);
        }
        .product-set-price {
          margin-top: var(--space-3);
          font-size: var(--font-size);
          line-height: 12px;
        }
      }
    }

    .carousel-bundle-mobile {
      width: 300px;
      flex-basis: 300px;
    }

    &.swiper-margin {
      margin-top: 40px;
      margin-bottom: 40px;
    }

    .carousel-button-next,
    .carousel-button-prev {
      position: absolute;
      visibility: hidden;
      opacity: 0;
      transition: visibility 0s linear 100ms, opacity 100ms ease-in;
      background-image: none;
      display: flex;
      font-size: 1em;
      width: 32px;
      height: 32px;
      top: 25%;
      padding: 8px;
      color: var(--link-color);
      margin-top: inherit;
      background-color: $white;
      justify-content: center;
      box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.2);
      border-radius: 50%;
      cursor: pointer;

      &.small-arrow {
        width: 22px;
        height: 22px;
      }

      @media (max-width: calc($tablet-breakpoint - 1px)) {
        display: none;
      }

      .af {
        align-self: center;
        padding: 10px;
      }
    }

    .carousel-button-next {
      right: 16px;
    }

    .carousel-button-prev {
      left: 16px;
    }

    /* purgecss ignore */
    &:hover {
      .carousel-button-next,
      .carousel-button-prev {
        visibility: visible;
        opacity: 1;
        transition: visibility 0s linear, opacity 200ms ease-out;

        &.disabled {
          pointer-events: initial;
          opacity: 50%;
        }
      }
    }
  }
}
</style>